import { RefObject, useCallback, useEffect, useRef } from 'react';
import {
  canonicalizePlaceholderBoundaryCaret,
  createPlaceholderUnit,
  expandRangeForAdjacentPlaceholderMutation,
  expandRangeForPlaceholderFormatting,
  expandRangeForPlaceholderMutation,
  getEditorSelectionRange,
  markKnownPlaceholders,
  rangeFullyCoversElementContents,
  rangeIntersectsPlaceholder,
  rangePartiallyIntersectsPlaceholder,
  rangeTouchesElementContentBoundary,
  selectionIsInsidePlaceholder,
  serializeProtectedEditorHtml,
  setProtectedEditorHtml,
  stabilizePlaceholderUnits,
} from '../utils/placeholderEditorDom';
import { isInlineTextFlowElement, mergeFragmentedPlaceholders } from '../utils/placeholderHtml';
import sanitizeTemplatePaste from '../utils/sanitizeTemplatePaste';

type ProtectedPlaceholderEditorOptions = {
  editorRef: RefObject<HTMLDivElement>;
  knownKeys: readonly string[];
  onChange: (html: string) => void;
  readOnly?: boolean;
};

type FormattingCommand = 'bold' | 'italic' | 'underline' | 'justifyLeft' | 'insertUnorderedList';

const BEFORE_INPUT_FORMAT_COMMANDS: Readonly<Record<string, FormattingCommand>> = {
  formatBold: 'bold',
  formatItalic: 'italic',
  formatUnderline: 'underline',
};

const SHORTCUT_FORMAT_COMMANDS: Readonly<Record<string, FormattingCommand>> = {
  b: 'bold',
  i: 'italic',
  u: 'underline',
};

function rangeFromStaticRange(ownerDocument: Document, staticRange: StaticRange): Range {
  const range = ownerDocument.createRange();
  range.setStart(staticRange.startContainer, staticRange.startOffset);
  range.setEnd(staticRange.endContainer, staticRange.endOffset);
  return range;
}

type DocumentWithCaretPosition = Document & {
  caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
};

function rangeAtPoint(ownerDocument: Document, x: number, y: number): Range | null {
  const range = ownerDocument.caretRangeFromPoint?.(x, y);
  if (range) return range;

  const position = (ownerDocument as DocumentWithCaretPosition).caretPositionFromPoint?.(x, y);
  if (!position) return null;
  const fallbackRange = ownerDocument.createRange();
  fallbackRange.setStart(position.offsetNode, position.offset);
  fallbackRange.collapse(true);
  return fallbackRange;
}

function rangeBelongsToEditor(editor: HTMLElement, range: Range): boolean {
  return (
    (range.startContainer === editor || editor.contains(range.startContainer)) &&
    (range.endContainer === editor || editor.contains(range.endContainer))
  );
}

function rangeAtEditorEnd(editor: HTMLElement): Range {
  const range = editor.ownerDocument.createRange();
  range.selectNodeContents(editor);
  range.collapse(false);
  return range;
}

function isMutationKey(event: React.KeyboardEvent<HTMLDivElement>): boolean {
  if (event.key === 'Backspace' || event.key === 'Delete' || event.key === 'Enter') return true;
  return event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;
}

function cloneRangeWithInlineContext(range: Range, editor: HTMLElement): DocumentFragment {
  const { ownerDocument } = editor;
  let contents = range.cloneContents();
  let context =
    range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
      ? (range.commonAncestorContainer as HTMLElement)
      : range.commonAncestorContainer.parentElement;

  while (context && context !== editor && isInlineTextFlowElement(context)) {
    const wrapper = context.cloneNode(false) as HTMLElement;
    wrapper.appendChild(contents);
    contents = ownerDocument.createDocumentFragment();
    contents.appendChild(wrapper);
    context = context.parentElement;
  }

  return contents;
}

function collectInlineAncestors(node: Node, editor: HTMLElement): HTMLElement[] {
  let current = node.nodeType === Node.ELEMENT_NODE ? (node as HTMLElement) : node.parentElement;
  const ancestors: HTMLElement[] = [];

  while (current && current !== editor && isInlineTextFlowElement(current)) {
    ancestors.push(current);
    current = current.parentElement;
  }

  return ancestors;
}

/** Includes fully selected inline wrappers so deletion does not leave empty formatting husks behind. */
function expandRangeForInlineAncestors(range: Range, editor: HTMLElement): void {
  if (range.collapsed) return;

  let expanded = true;
  while (expanded) {
    expanded = false;
    const candidates = new Set([
      ...collectInlineAncestors(range.startContainer, editor),
      ...collectInlineAncestors(range.endContainer, editor),
    ]);

    const candidate = Array.from(candidates).find((current) => {
      if (!rangeFullyCoversElementContents(editor, range, current)) return false;
      return (
        range.startContainer === current ||
        current.contains(range.startContainer) ||
        range.endContainer === current ||
        current.contains(range.endContainer)
      );
    });
    if (candidate) {
      const startsInside = range.startContainer === candidate || candidate.contains(range.startContainer);
      const endsInside = range.endContainer === candidate || candidate.contains(range.endContainer);
      if (startsInside) range.setStartBefore(candidate);
      if (endsInside) range.setEndAfter(candidate);
      expanded = true;
    }
  }
}

function commonInlineAncestors(range: Range, editor: HTMLElement): HTMLElement[] {
  const endAncestors = new Set(collectInlineAncestors(range.endContainer, editor));
  return collectInlineAncestors(range.startContainer, editor).filter((candidate) => endAncestors.has(candidate));
}

function wrapFragmentWithBoundaryInlineContext(
  range: Range,
  editor: HTMLElement,
  fragment: DocumentFragment,
): DocumentFragment {
  let wrapped = fragment;
  commonInlineAncestors(range, editor).forEach((candidate) => {
    if (rangeFullyCoversElementContents(editor, range, candidate)) return;
    if (
      !rangeTouchesElementContentBoundary(editor, range, candidate, 'start') &&
      !rangeTouchesElementContentBoundary(editor, range, candidate, 'end')
    )
      return;

    const wrapper = candidate.cloneNode(false) as HTMLElement;
    wrapper.appendChild(wrapped);
    wrapped = editor.ownerDocument.createDocumentFragment();
    wrapped.appendChild(wrapper);
  });
  return wrapped;
}

function findSurvivingInlineCaretContext(
  range: Range,
  editor: HTMLElement,
): { element: HTMLElement; atStart: boolean } | null {
  const candidate = commonInlineAncestors(range, editor).find(
    (current) =>
      !rangeFullyCoversElementContents(editor, range, current) &&
      (rangeTouchesElementContentBoundary(editor, range, current, 'start') ||
        rangeTouchesElementContentBoundary(editor, range, current, 'end')),
  );
  return candidate
    ? { element: candidate, atStart: rangeTouchesElementContentBoundary(editor, range, candidate, 'start') }
    : null;
}

function hasOnlyEmptyTextNodes(element: HTMLElement): boolean {
  return Array.from(element.childNodes).every(
    (child) => child.nodeType === Node.TEXT_NODE && (child.textContent ?? '') === '',
  );
}

function runEditingCommand(
  editor: HTMLElement,
  units: HTMLElement[],
  command: 'delete' | 'insertHTML',
  value?: string,
): boolean {
  units.forEach((unit) => unit.setAttribute('contenteditable', 'true'));
  try {
    return editor.ownerDocument.execCommand?.(command, false, value) ?? false;
  } catch {
    return false;
  } finally {
    units.forEach((unit) => {
      if (unit.isConnected) unit.setAttribute('contenteditable', 'false');
    });
  }
}

function deleteRangeContents(range: Range, editor: HTMLElement, units: HTMLElement[]): void {
  const cleanupCandidates = new Set([
    ...collectInlineAncestors(range.startContainer, editor),
    ...collectInlineAncestors(range.endContainer, editor),
  ]);
  expandRangeForInlineAncestors(range, editor);
  const caretContext = findSurvivingInlineCaretContext(range, editor);
  const selection = editor.ownerDocument.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);

  const deletedWithHistory = runEditingCommand(editor, units, 'delete');

  if (!deletedWithHistory) {
    range.deleteContents();
    range.collapse(true);
    selection?.removeAllRanges();
    selection?.addRange(range);
  }

  cleanupCandidates.forEach((candidate) => {
    if (editor.contains(candidate) && hasOnlyEmptyTextNodes(candidate)) candidate.parentNode?.removeChild(candidate);
  });

  if (caretContext?.element.isConnected) {
    const caret = editor.ownerDocument.createRange();
    caret.selectNodeContents(caretContext.element);
    caret.collapse(caretContext.atStart);
    selection?.removeAllRanges();
    selection?.addRange(caret);
  }
}

function insertRangeContents(
  range: Range,
  editor: HTMLElement,
  units: HTMLElement[],
  fragment: DocumentFragment,
  touchesPlaceholder: boolean,
): void {
  const cleanupCandidates = new Set([
    ...collectInlineAncestors(range.startContainer, editor),
    ...collectInlineAncestors(range.endContainer, editor),
  ]);
  if (touchesPlaceholder) expandRangeForInlineAncestors(range, editor);
  const insertionFragment = touchesPlaceholder
    ? wrapFragmentWithBoundaryInlineContext(range, editor, fragment)
    : fragment;

  const selection = editor.ownerDocument.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);

  const scratch = editor.ownerDocument.createElement('div');
  scratch.appendChild(insertionFragment.cloneNode(true));
  if (runEditingCommand(editor, units, 'insertHTML', scratch.innerHTML)) return;

  const lastNode = insertionFragment.lastChild;
  range.deleteContents();
  range.insertNode(insertionFragment);
  cleanupCandidates.forEach((candidate) => {
    if (editor.contains(candidate) && hasOnlyEmptyTextNodes(candidate)) candidate.parentNode?.removeChild(candidate);
  });
  if (lastNode) {
    range.setStartAfter(lastNode);
    range.collapse(true);
    selection?.removeAllRanges();
    selection?.addRange(range);
  }
}

function serializeRangeContents(range: Range, editor: HTMLElement): { html: string; text: string } {
  const scratch = range.startContainer.ownerDocument?.createElement('div') ?? document.createElement('div');
  scratch.appendChild(cloneRangeWithInlineContext(range, editor));
  return { html: serializeProtectedEditorHtml(scratch), text: range.toString() };
}

export default function useProtectedPlaceholderEditor({
  editorRef,
  knownKeys,
  onChange,
  readOnly = false,
}: ProtectedPlaceholderEditorOptions) {
  const onChangeRef = useRef(onChange);
  const readOnlyRef = useRef(readOnly);
  const knownKeysRef = useRef(knownKeys);
  const formatRef = useRef<(command: FormattingCommand) => void>(() => undefined);
  const formattingInProgress = useRef(false);
  const internalDragInProgress = useRef(false);
  onChangeRef.current = onChange;
  readOnlyRef.current = readOnly;
  knownKeysRef.current = knownKeys;

  const syncContent = useCallback(() => {
    if (readOnlyRef.current) return;
    const editor = editorRef.current;
    if (!editor) return;
    stabilizePlaceholderUnits(editor, knownKeysRef.current);
    onChangeRef.current(serializeProtectedEditorHtml(editor));
  }, [editorRef]);

  const getHtml = useCallback((): string => {
    const editor = editorRef.current;
    return editor ? serializeProtectedEditorHtml(editor) : '';
  }, [editorRef]);

  const setHtml = useCallback(
    (html: string): void => {
      const editor = editorRef.current;
      if (editor) setProtectedEditorHtml(editor, html, knownKeysRef.current);
    },
    [editorRef],
  );

  const format = useCallback(
    (command: FormattingCommand): void => {
      if (readOnlyRef.current) return;
      const editor = editorRef.current;
      if (!editor || formattingInProgress.current) return;

      const range = getEditorSelectionRange(editor);
      const units = range ? expandRangeForPlaceholderFormatting(editor, range, knownKeysRef.current) : [];
      units.forEach((unit) => unit.setAttribute('contenteditable', 'true'));
      formattingInProgress.current = true;

      try {
        editor.ownerDocument.execCommand(command, false);
      } finally {
        formattingInProgress.current = false;
        units.forEach((unit) => unit.setAttribute('contenteditable', 'false'));
        syncContent();
      }
    },
    [editorRef, syncContent],
  );
  formatRef.current = format;

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return undefined;
    const ownerWindow = editor.ownerDocument.defaultView;
    let selectionRestoreTimer: number | undefined;
    let pendingSelection: Range | null = null;

    const restoreSelection = (): void => {
      ownerWindow?.clearTimeout(selectionRestoreTimer);
      const range = pendingSelection;
      pendingSelection = null;
      if (range && editor.ownerDocument.activeElement === editor && rangeBelongsToEditor(editor, range)) {
        const selection = editor.ownerDocument.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    };

    const preventPlaceholderMutation = (event: InputEvent): void => {
      if (event.cancelable) event.preventDefault();

      const selection = editor.ownerDocument.getSelection();
      if (!selection?.rangeCount || !ownerWindow) return;
      pendingSelection = selection.getRangeAt(0).cloneRange();
      // IME updates (including some cancellations) ignore preventDefault. Remove their editing target instead.
      selection.removeAllRanges();
      ownerWindow.clearTimeout(selectionRestoreTimer);
      // A microtask runs before the browser mutation; restore only in the following task.
      selectionRestoreTimer = ownerWindow.setTimeout(restoreSelection, 0);
    };

    const handleNativeBeforeInput = (event: InputEvent): void => {
      if (readOnlyRef.current) {
        event.preventDefault();
        return;
      }
      restoreSelection();
      const formatCommand = BEFORE_INPUT_FORMAT_COMMANDS[event.inputType];
      if (formatCommand && !formattingInProgress.current) {
        if (event.cancelable) event.preventDefault();
        formatRef.current(formatCommand);
        return;
      }

      if (event.inputType === 'historyUndo' || event.inputType === 'historyRedo') return;

      const selectionRange = getEditorSelectionRange(editor);
      if (
        selectionRange &&
        (selectionIsInsidePlaceholder(editor, selectionRange, knownKeysRef.current) ||
          rangePartiallyIntersectsPlaceholder(editor, selectionRange, knownKeysRef.current))
      ) {
        preventPlaceholderMutation(event);
        return;
      }

      const targetRanges = typeof event.getTargetRanges === 'function' ? event.getTargetRanges() : [];
      const hasForbiddenTarget = Array.from(targetRanges).some((targetRange) => {
        const range = rangeFromStaticRange(editor.ownerDocument, targetRange);
        return (
          selectionIsInsidePlaceholder(editor, range, knownKeysRef.current) ||
          rangePartiallyIntersectsPlaceholder(editor, range, knownKeysRef.current)
        );
      });
      if (hasForbiddenTarget) {
        preventPlaceholderMutation(event);
        return;
      }

      if (!selectionRange) return;
      if (selectionRange.collapsed) canonicalizePlaceholderBoundaryCaret(editor, selectionRange);
      else expandRangeForPlaceholderMutation(editor, selectionRange, knownKeysRef.current);
    };

    editor.addEventListener('beforeinput', handleNativeBeforeInput);
    editor.addEventListener('keydown', restoreSelection);
    editor.addEventListener('pointerdown', restoreSelection);
    return () => {
      editor.removeEventListener('beforeinput', handleNativeBeforeInput);
      editor.removeEventListener('keydown', restoreSelection);
      editor.removeEventListener('pointerdown', restoreSelection);
      ownerWindow?.clearTimeout(selectionRestoreTimer);
    };
  }, [editorRef]);

  const handleInput = useCallback((): void => {
    syncContent();
  }, [syncContent]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>): void => {
      const shortcut =
        (event.ctrlKey || event.metaKey) && !event.altKey ? SHORTCUT_FORMAT_COMMANDS[event.key.toLowerCase()] : null;
      if (readOnlyRef.current) {
        if (shortcut || isMutationKey(event)) event.preventDefault();
        return;
      }
      if (shortcut) {
        event.preventDefault();
        format(shortcut);
        return;
      }

      if (!isMutationKey(event)) return;
      const editor = editorRef.current;
      if (!editor) return;
      const range = getEditorSelectionRange(editor);
      if (!range) return;

      if (
        selectionIsInsidePlaceholder(editor, range, knownKeysRef.current) ||
        rangePartiallyIntersectsPlaceholder(editor, range, knownKeysRef.current)
      ) {
        event.preventDefault();
        return;
      }

      if (range.collapsed) {
        if (event.key === 'Backspace' || event.key === 'Delete') {
          const units = expandRangeForAdjacentPlaceholderMutation(
            editor,
            range,
            event.key === 'Backspace' ? 'backward' : 'forward',
            knownKeysRef.current,
          );
          if (!range.collapsed) {
            event.preventDefault();
            deleteRangeContents(range, editor, units);
            syncContent();
            return;
          }
        }
        canonicalizePlaceholderBoundaryCaret(editor, range);
        return;
      }

      const touchesPlaceholder = rangeIntersectsPlaceholder(editor, range, knownKeysRef.current);
      const units = expandRangeForPlaceholderMutation(editor, range, knownKeysRef.current);
      if (touchesPlaceholder && (event.key === 'Backspace' || event.key === 'Delete')) {
        event.preventDefault();
        deleteRangeContents(range, editor, units);
        syncContent();
      }
    },
    [editorRef, format, syncContent],
  );

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLDivElement>): void => {
      event.preventDefault();
      if (readOnlyRef.current) return;
      const editor = editorRef.current;
      if (!editor) return;

      const range = getEditorSelectionRange(editor);
      if (
        range &&
        (selectionIsInsidePlaceholder(editor, range, knownKeysRef.current) ||
          rangePartiallyIntersectsPlaceholder(editor, range, knownKeysRef.current))
      ) {
        return;
      }

      const scratch = editor.ownerDocument.createElement('div');
      scratch.appendChild(sanitizeTemplatePaste(event.clipboardData, editor.ownerDocument, knownKeysRef.current));
      scratch.innerHTML = mergeFragmentedPlaceholders(scratch.innerHTML, knownKeysRef.current);
      markKnownPlaceholders(scratch, knownKeysRef.current);

      const fragment = editor.ownerDocument.createDocumentFragment();
      while (scratch.firstChild) fragment.appendChild(scratch.firstChild);
      if (!fragment.firstChild) return;
      if (range) {
        if (range.collapsed) canonicalizePlaceholderBoundaryCaret(editor, range);
        const touchesPlaceholder = !range.collapsed && rangeIntersectsPlaceholder(editor, range, knownKeysRef.current);
        const units = range.collapsed ? [] : expandRangeForPlaceholderMutation(editor, range, knownKeysRef.current);
        insertRangeContents(range, editor, units, fragment, touchesPlaceholder);
      } else {
        editor.appendChild(fragment);
      }

      syncContent();
    },
    [editorRef, syncContent],
  );

  const handleCut = useCallback(
    (event: React.ClipboardEvent<HTMLDivElement>): void => {
      if (readOnlyRef.current) {
        event.preventDefault();
        return;
      }
      const editor = editorRef.current;
      if (!editor) return;
      const range = getEditorSelectionRange(editor);
      if (!range || range.collapsed) return;

      if (rangePartiallyIntersectsPlaceholder(editor, range, knownKeysRef.current)) {
        event.preventDefault();
        return;
      }

      const touchesPlaceholder = rangeIntersectsPlaceholder(editor, range, knownKeysRef.current);
      if (!touchesPlaceholder) return;
      const units = expandRangeForPlaceholderMutation(editor, range, knownKeysRef.current);

      const serialized = serializeRangeContents(range, editor);
      try {
        event.clipboardData.setData('text/plain', serialized.text);
        event.clipboardData.setData('text/html', serialized.html);
      } catch {
        return;
      }

      event.preventDefault();
      deleteRangeContents(range, editor, units);
      syncContent();
    },
    [editorRef, syncContent],
  );

  const handleDragStart = useCallback(
    (event: React.DragEvent<HTMLDivElement>): void => {
      internalDragInProgress.current = false;
      if (readOnlyRef.current) {
        event.preventDefault();
        return;
      }
      const editor = editorRef.current;
      if (!editor) return;
      const range = getEditorSelectionRange(editor);
      if (!range) return;
      if (rangePartiallyIntersectsPlaceholder(editor, range, knownKeysRef.current)) {
        event.preventDefault();
        return;
      }
      expandRangeForPlaceholderMutation(editor, range, knownKeysRef.current);
      internalDragInProgress.current = true;
    },
    [editorRef],
  );

  const handleDragEnd = useCallback((): void => {
    internalDragInProgress.current = false;
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>): void => {
      if (readOnlyRef.current) {
        event.preventDefault();
        internalDragInProgress.current = false;
        return;
      }
      const editor = editorRef.current;
      if (!editor) return;
      const pointRange = rangeAtPoint(editor.ownerDocument, event.clientX, event.clientY);
      const dropRange = pointRange && rangeBelongsToEditor(editor, pointRange) ? pointRange : rangeAtEditorEnd(editor);
      if (
        selectionIsInsidePlaceholder(editor, dropRange, knownKeysRef.current) ||
        rangePartiallyIntersectsPlaceholder(editor, dropRange, knownKeysRef.current)
      ) {
        event.preventDefault();
        internalDragInProgress.current = false;
        return;
      }

      if (internalDragInProgress.current) {
        internalDragInProgress.current = false;
        return;
      }

      event.preventDefault();
      const scratch = editor.ownerDocument.createElement('div');
      scratch.appendChild(sanitizeTemplatePaste(event.dataTransfer, editor.ownerDocument, knownKeysRef.current));
      scratch.innerHTML = mergeFragmentedPlaceholders(scratch.innerHTML, knownKeysRef.current);
      markKnownPlaceholders(scratch, knownKeysRef.current);

      const fragment = editor.ownerDocument.createDocumentFragment();
      while (scratch.firstChild) fragment.appendChild(scratch.firstChild);
      if (!fragment.firstChild) return;
      canonicalizePlaceholderBoundaryCaret(editor, dropRange);
      insertRangeContents(dropRange, editor, [], fragment, false);
      syncContent();
    },
    [editorRef, syncContent],
  );

  const insertPlaceholder = useCallback(
    (key: string): void => {
      if (readOnlyRef.current) return;
      const editor = editorRef.current;
      if (!editor || !knownKeysRef.current.includes(key)) return;
      editor.focus();

      const range = getEditorSelectionRange(editor);
      const unit = createPlaceholderUnit(key, editor.ownerDocument);

      if (!range) {
        editor.appendChild(unit);
      } else {
        if (
          selectionIsInsidePlaceholder(editor, range, knownKeysRef.current) ||
          rangePartiallyIntersectsPlaceholder(editor, range, knownKeysRef.current)
        )
          return;
        if (range.collapsed) canonicalizePlaceholderBoundaryCaret(editor, range);
        const touchesPlaceholder = !range.collapsed && rangeIntersectsPlaceholder(editor, range, knownKeysRef.current);
        const units = range.collapsed ? [] : expandRangeForPlaceholderMutation(editor, range, knownKeysRef.current);
        const fragment = editor.ownerDocument.createDocumentFragment();
        fragment.appendChild(unit);
        insertRangeContents(range, editor, units, fragment, touchesPlaceholder);
      }

      syncContent();
    },
    [editorRef, syncContent],
  );

  return {
    format,
    getHtml,
    handleCut,
    handleDragEnd,
    handleDragStart,
    handleDrop,
    handleInput,
    handleKeyDown,
    handlePaste,
    insertPlaceholder,
    setHtml,
  };
}
